<template>
  <div v-if="!isLoading" class="p-4 max-w-xl mx-auto">
    <!-- Back Button -->
    <div class="flex justify-between items-center mb-4">
      <div
        @click="$router.back()"
        class="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 cursor-pointer shadow-md transition"
      >
        <BackSvg />
      </div>
    </div>

    <!-- Title -->
    <div class="flex flex-col justify-center items-center mb-4">
      <h2 class="text-lg text-zinc-700 text-center font-semibold">
        Uploading Attachment
      </h2>
      <h4 class="text-sm text-zinc-500">#{{ transactionId }}</h4>
    </div>

    <!-- Upload Section -->
    <form @submit.prevent="submitHandler()">
      <div class="relative my-4">
        <label
          for="file-upload"
          class="flex flex-col items-center justify-center w-full h-36 px-4 transition bg-white border-2 border-dashed rounded-2xl cursor-pointer border-gray-300 hover:border-blue-400 focus:outline-none"
        >
          <div class="flex flex-col items-center justify-center space-y-2">
            <svg
              class="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M7 16V4m0 0l-3 3m3-3l3 3M17 8v8a4 4 0 01-4 4H7a4 4 0 01-4-4V8a4 4 0 014-4h3"
              />
            </svg>
            <p class="text-sm text-gray-600 text-center">
              <span class="font-medium text-blue-600"
                >Please upload your medical supporting documents.</span
              >
              or drag and drop<br />
              PNG, JPG, PDF, DOC, DOCX up to 15MB
            </p>
          </div>

          <input
            id="file-upload"
            type="file"
            accept="image/*,application/pdf,.doc,.docx"
            class="hidden"
            multiple
            required
            @change="handleFileUpload"
          />
        </label>
      </div>

      <!-- Preview Section -->
      <div v-if="selectedFiles.length" class="mt-4">
        <p class="text-sm text-gray-500 mb-2 text-center">Preview:</p>
        <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div
            v-for="(file, index) in selectedFiles"
            :key="index"
            class="relative flex flex-col items-center p-2 border rounded shadow"
          >
            <!-- Image Preview -->
            <template v-if="file.type.startsWith('image/')">
              <img :src="file.url" class="w-40 h-28 object-cover rounded" />
            </template>

            <!-- Document Preview -->
            <template v-else>
              <div
                class="flex flex-col items-center justify-center w-40 h-28 bg-gray-100 rounded p-2 text-center"
              >
                <p class="text-sm text-gray-700 break-words">
                  {{ file.name }}
                </p>
                <p class="text-xs text-gray-500">Document</p>
              </div>
            </template>

            <!-- Remove Button -->
            <button
              @click="removeFile(index)"
              class="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-700"
              type="button"
            >
              &times;
            </button>
          </div>
        </div>
      </div>

      <!-- Agreement Checkbox -->
      <div class="mt-4 flex items-center justify-start space-x-2">
        <input
          type="checkbox"
          v-model="hasAgreement"
          class="w-4 h-4 text-blue-600 border-gray-300 rounded"
          required
        />
        <label for="agreement" class="text-sm text-gray-700">
          I acknowledge that I have read and agree to the
          <span
            class="font-medium text-blue-600"
            @click="modalTermAndCondition()"
            >terms and conditions</span
          >.
        </label>
      </div>

      <div class="mt-4 flex items-start space-x-2">
        <button
          type="submit"
          class="w-full py-3 bg-primary hover:bg-hover text-white font-semibold rounded-lg transition duration-200 flex items-center justify-center"
        >
          <span v-if="!loading">Submit</span>
          <span v-else>
            <svg
              class="animate-spin h-5 w-5 mr-2 text-white inline-block"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
            uploading...
          </span>
        </button>
      </div>
    </form>
  </div>

  <!-- Loading Spinner -->
  <div v-else class="flex items-center justify-center h-48">
    <svg
      class="w-10 h-10 animate-spin text-orange-600"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        class="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="4"
      ></circle>
      <path
        class="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      ></path>
    </svg>
  </div>
</template>

<script>
import { ref, onMounted, inject } from "vue";
import { useRoute } from "vue-router";
import BackSvg from "@/components/svgs/BackSvg.vue";

export default {
  components: { BackSvg },
  setup() {
    const axios = inject("$axios");
    const swal = inject("$swal");
    const router = inject("$router");
    const route = useRoute();
    const transactionId = ref("");
    const isLoading = ref(false);
    const loading = ref(false);
    const selectedFiles = ref([]);
    const hasAgreement = ref(false);

    // Handle file upload
    const handleFileUpload = (event) => {
      const files = event.target.files;
      if (!files) return;

      Array.from(files).forEach((file) => {
        const fileData = {
          name: file.name,
          type: file.type,
          url: "",
        };

        // If image, create preview
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            fileData.url = e.target.result;
            selectedFiles.value.push(fileData);
          };
          reader.readAsDataURL(file);
        } else {
          selectedFiles.value.push(fileData);
        }
      });
    };

    // Remove file
    const removeFile = (index) => {
      selectedFiles.value.splice(index, 1);
    };

    onMounted(() => {
      transactionId.value = route.params.id;
    });
    const submitHandler = async () => {
      console.log("Triggered");

      // Show confirmation modal
      const result = await swal.fire({
        title: "Confirm Submission",
        text: "Are you sure you want to upload the selected files?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, upload",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#f59e0b",
        cancelButtonColor: "#6b7280",
      });

      if (!result.isConfirmed) {
        // User clicked Cancel
        return;
      }

      loading.value = true;

      try {
        const formData = new FormData();

        // Append transaction ID
        formData.append("transactionid", transactionId.value);

        // Append selected files
        const fileInput = document.getElementById("file-upload").files;
        Array.from(fileInput).forEach((file) => {
          formData.append("files", file);
        });

        const response = await axios.post("api/filemanager/upload", formData);
        console.log(response);

        // Success alert
        swal
          .fire({
            icon: "success",
            title: "Uploaded!",
            text: "Files uploaded successfully.",
            confirmButtonColor: "#f59e0b",
          })
          .then(() => {
            router.push(`/portal/transaction/${transactionId.value}`);
          });
      } catch (error) {
        console.error(error);
        swal.fire({
          icon: "error",
          title: "Error",
          text: "An error occurred while uploading files.",
          confirmButtonColor: "#f59e0b",
        });
      } finally {
        loading.value = false;
      }
    };

    const modalTermAndCondition = () => {
      swal.fire({
        icon: "info",
        title: "Terms and Conditions",
        html: `
    <div style="max-height: 400px; overflow-y: auto; text-align: justify; padding-right: 10px; font-size: 14px;">
      <p>By uploading your medical documents, you agree to the following:</p>
      <br/>
      <p><b>I. Accuracy of Information</b></p>
      <p>
        All information and documents provided must be accurate, complete, and truthful.
        You are responsible for ensuring the documents are valid and current.
      </p>
      <br/>
      <p><b>II. Confidentiality and Privacy</b></p>
      <p>
        Your documents will be used solely for the purpose of processing your medical transaction or request.
        We are committed to protecting your personal and medical information in accordance with our Privacy Policy and applicable data protection laws.
      </p>
      <br/>
      <p><b>III. File Responsibility</b></p>
      <p>
        You acknowledge that we are not responsible for incomplete, corrupted, or incorrectly uploaded files.
        It is your responsibility to verify that all documents are legible and properly uploaded.
      </p>
      <br/>
      <p><b>IV. Security Measures</b></p>
      <p>
        Uploaded files will be stored securely, and access is restricted to authorized personnel only.
        Despite our security measures, no system can guarantee absolute security of digital files.
      </p>
      <br/>
      <p><b>V. Consent</b></p>
      <p>
        By uploading, you consent to the collection, storage, and use of your medical documents for the purpose stated above.
        You agree to comply with all instructions regarding document submission and handling.
      </p>
      <br/>
      <p><b>VI. Liability</b></p>
      <p>
        We are not liable for any consequences arising from the misuse of the documents or incorrect submission.
      </p>
     <br/>
      <p><b>VII. Legal Compliance</b></p>
      <p>
        You confirm that the uploaded documents do not violate any law or regulation and that you have the authority to submit them.
      </p>
     <br/>
      <p>Please read carefully before proceeding. By clicking “I Agree,” you confirm that you have understood and accepted these terms.</p>
    </div>
  `,
        confirmButtonText: "I Agree",
        confirmButtonColor: "#f59e0b",
        width: 600,
        scrollbarPadding: false,
      });
    };

    return {
      transactionId,
      isLoading,
      selectedFiles,
      hasAgreement,
      loading,
      handleFileUpload,
      removeFile,
      submitHandler,
      modalTermAndCondition,
    };
  },
};
</script>
